// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { toast } from 'sonner';
import { Shield, User as UserIcon, UserPlus, Search, Trash2 } from 'lucide-react';
import { useUsersApi, type UsersApi } from '../../services/users-api';
import { useWorkspaceApi, type WorkspaceApi } from '../../services/workspace-api';
import type { UserProfileResponse } from '../../types/user';
import { AddUserDialog } from '../users/AddUserDialog';
import { UserRemovalDialog } from '../users/UserRemovalDialog';

interface WorkspaceMembersPanelProps {
  orgId: string;
  wsId: string;
  usersApi?: UsersApi;
  workspaceApi?: WorkspaceApi;
}

interface WorkspaceMemberWithRole extends UserProfileResponse {
  role?: string;
}

export function WorkspaceMembersPanel({
  orgId,
  wsId,
  usersApi: injectedUsersApi,
  workspaceApi: injectedWorkspaceApi,
}: WorkspaceMembersPanelProps) {
  const defaultUsersApi = useUsersApi();
  const defaultWorkspaceApi = useWorkspaceApi();
  const usersApi = injectedUsersApi ?? defaultUsersApi;
  const workspaceApi = injectedWorkspaceApi ?? defaultWorkspaceApi;
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removalUserId, setRemovalUserId] = useState<string | null>(null);

  const { data: members = [], isLoading } = useQuery<WorkspaceMemberWithRole[]>({
    queryKey: ['workspace-members', orgId, wsId],
    queryFn: async () => {
      const users = await workspaceApi.getWorkspaceUsers(orgId, wsId);
      return users.map((user) => {
        const roles = (user as unknown as { roles?: string[] }).roles;
        const role = roles?.includes('admin') ? 'admin' : 'member';
        return { ...user, role };
      });
    },
  });

  // Pull all users for typeahead suggestions; falls back to empty (e.g. 403) for non-global admins.
  const { data: allUsers = [] } = useQuery<UserProfileResponse[]>({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.getAllUsers(),
    retry: false,
    throwOnError: false,
  });

  const allUserIds = React.useMemo(() => allUsers.map(u => u.userId), [allUsers]);
  const memberUserIds = React.useMemo(() => members.map(m => m.userId), [members]);

  const addMemberMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      usersApi.addUserToWorkspace(orgId, wsId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', orgId, wsId] });
      toast.success('User added to workspace');
      setAddDialogOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to add user', {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      usersApi.removeUserFromWorkspace(orgId, wsId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', orgId, wsId] });
      toast.success('Member removed from workspace');
      setRemovalUserId(null);
    },
    onError: (error) => {
      toast.error('Failed to remove member', {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      usersApi.updateUserWorkspaceRole(orgId, wsId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', orgId, wsId] });
      toast.success('Member role updated');
    },
    onError: (error) => {
      toast.error('Failed to update role', {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const filteredMembers = React.useMemo(() => {
    if (!searchQuery) return members;
    const query = searchQuery.toLowerCase();
    return members.filter((member) => member.userId.toLowerCase().includes(query));
  }, [members, searchQuery]);

  return (
    <div data-testid="workspace-members" className="workspace-members">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-[15px] text-[#64748b]" data-testid="workspace-members-heading">Manage users in this workspace</p>
          <Button data-testid="user-add-button" className="h-[42px] rounded-[10px] bg-[#1e293b] px-[18px] text-sm font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.12)] transition-[background,transform] duration-150 hover:bg-[#0f172a] active:scale-[0.97] has-[>svg]:px-[18px]" onClick={() => setAddDialogOpen(true)}>
            <UserPlus className="size-[17px]" />
            Add User
          </Button>
        </div>

        <div className="w-full">
          <div className="flex h-[46px] items-center gap-[11px] rounded-[10px] border border-[#e2e8f0] bg-white px-[14px] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow] duration-[120ms] focus-within:border-[#93c5fd] focus-within:shadow-[0_0_0_3px_rgba(37,99,235,0.08)]">
            <Search className="size-[17px] shrink-0 text-[#94a3b8]" />
            <input
              data-testid="user-search"
              type="search"
              placeholder="Search users…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-[15px] leading-[normal] text-[#0f172a] outline-none placeholder:text-[#94a3b8]"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Loading members...</div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="user-no-results">
            {searchQuery ? 'No members found matching your search.' : 'No members in this workspace.'}
          </div>
        ) : (
          <div data-testid="workspace-member-list-card" className="overflow-hidden rounded-[14px] border border-[#e2e8f0] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <div className="px-6 pb-1 pt-5">
              <h2 className="text-lg font-bold leading-[normal] text-[#0f172a]">Members</h2>
              <p className="mt-0.5 text-[13.5px] leading-[normal] text-[#64748b]">
                {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''} found
              </p>
            </div>
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-10 px-6 text-xs font-semibold text-[#475569]">User</TableHead>
                      <TableHead className="h-10 w-[220px] px-4 text-xs font-semibold text-[#475569]">Role</TableHead>
                      <TableHead className="h-10 w-[90px] px-6 text-right text-xs font-semibold text-[#475569]">Remove</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.map((member, index) => (
                      <TableRow key={member.userId} data-testid={`user-row-${member.userId}`} className="docreview-user-row hover:bg-[#fafbfc]" style={{ animationDelay: `${Math.min(index, 14) * 22}ms` }}>
                        <TableCell className="px-6 py-[13px] font-medium">
                          <div className="flex items-center gap-[11px]">
                            <UserIcon className="size-[17px] text-[#94a3b8]" />
                            <div className="text-[14.5px] font-normal text-[#0f172a]">{member.userId}</div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-[13px]">
                          <div className="flex items-center gap-[9px]">
                            {member.role === 'admin' && (
                              <Shield className="size-[15px] text-blue-700" />
                            )}
                            <Select
                              value={member.role || 'member'}
                              onValueChange={(role) => updateRoleMutation.mutate({ userId: member.userId, role })}
                              disabled={updateRoleMutation.isPending}
                            >
                              <SelectTrigger
                                aria-label={`Role for ${member.userId}`}
                                className="h-[38px] w-[200px] rounded-[9px] border-[#e2e8f0] bg-white px-[13px] text-sm text-[#0f172a] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow] duration-[120ms] focus-visible:border-[#93c5fd] focus-visible:ring-[3px] focus-visible:ring-blue-600/10"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="min-w-[200px] rounded-[14px] border-[#d8dee8] bg-white shadow-[0_14px_34px_rgba(15,23,42,0.18)]">
                                <SelectItem value="member" className="h-10 rounded-[10px] py-0 pl-9 pr-3 text-base focus:bg-blue-500 focus:text-white [&_[data-slot=select-item-indicator]]:left-3 [&_[data-slot=select-item-indicator]]:right-auto">
                                  Workspace member
                                </SelectItem>
                                <SelectItem value="admin" className="h-10 rounded-[10px] py-0 pl-9 pr-3 text-base focus:bg-blue-500 focus:text-white [&_[data-slot=select-item-indicator]]:left-3 [&_[data-slot=select-item-indicator]]:right-auto">
                                  Workspace admin
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-[13px] text-right">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove ${member.userId} from workspace`}
                            data-testid={`workspace-user-remove-${member.userId}`}
                            onClick={() => setRemovalUserId(member.userId)}
                            disabled={removeMemberMutation.isPending}
                            className="size-[34px] rounded-lg text-red-500 transition-[background,transform] duration-[120ms] hover:bg-red-50 hover:text-red-600 active:scale-90"
                          >
                            <Trash2 className="size-[17px]" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
          </div>
        )}
      </div>

      <AddUserDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        scope="workspace"
        allUserIds={allUserIds}
        scopeUserIds={memberUserIds}
        isPending={addMemberMutation.isPending}
        onSubmit={({ userId, role }) =>
          addMemberMutation.mutate({ userId, role: role ?? 'member' })
        }
      />

      <UserRemovalDialog
        userId={removalUserId}
        scope="workspace"
        isPending={removeMemberMutation.isPending}
        onOpenChange={(open) => {
          if (!open && !removeMemberMutation.isPending) setRemovalUserId(null);
        }}
        onConfirm={() => {
          if (removalUserId) removeMemberMutation.mutate(removalUserId);
        }}
      />
    </div>
  );
}
